import { useState } from "react";

type AuthMode = "login" | "signup";
type ContactMethod = "email" | "phone";

export interface CommunityUser {
  name: string;
  contact: string;
  method: ContactMethod;
  whatsapp: string;
  role: string;
  avatar: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: CommunityUser) => void;
}

const roleOptions = [
  "Doctor / Physician",
  "Psychologist / Counselor",
  "Nutritionist",
  "Social Worker",
  "Nurse",
  "Volunteer",
  "Business Owner",
  "Parent / Family",
  "Student",
  "Other",
];

const avatarColors = [
  "bg-teal-400", "bg-rose-400", "bg-amber-400",
  "bg-green-400", "bg-pink-400", "bg-orange-400",
];

export default function CommunityAuthModal({ isOpen, onClose, onSuccess }: Props) {
  const [mode, setMode] = useState<AuthMode>("signup");
  const [method, setMethod] = useState<ContactMethod>("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [role, setRole] = useState(roleOptions[9]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (mode === "signup") {
      if (!name.trim()) errs.name = "Name is required";
    }
    if (method === "email") {
      if (!email.trim()) errs.contact = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.contact = "Enter a valid email";
    } else {
      if (!phone.trim()) errs.contact = "Phone number is required";
      else if (!/^\+?[\d\s\-()]{7,}$/.test(phone)) errs.contact = "Enter a valid phone number";
    }
    if (!password) errs.password = "Password is required";
    else if (password.length < 6) errs.password = "At least 6 characters";
    if (mode === "signup" && password !== confirmPassword) errs.confirm = "Passwords do not match";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const contact = method === "email" ? email : phone;
    const initials = name.trim().slice(0, 2).toUpperCase() || contact.slice(0, 2).toUpperCase();
    const colorClass = avatarColors[Math.floor(Math.random() * avatarColors.length)];
    onSuccess({
      name: mode === "signup" ? name.trim() : contact.split("@")[0],
      contact,
      method,
      whatsapp: whatsapp.trim(),
      role,
      avatar: `${initials}|${colorClass}`,
    });
    onClose();
  };

  const switchMode = (m: AuthMode) => {
    setMode(m);
    setErrors({});
    setPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden">
        {/* Pink top accent */}
        <div className="h-1.5 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 w-full"></div>

        <div className="p-7 max-h-[90vh] overflow-y-auto">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-pink-100 mx-auto mb-3">
              <i className="ri-heart-fill text-pink-500 text-2xl"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {mode === "signup" ? "Join Our Community" : "Welcome Back"}
            </h2>
            <p className="text-stone-500 text-sm mt-1">
              {mode === "signup"
                ? "Connect, help others, and make the world better — for free"
                : "Sign in to post, connect, and help others"}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-stone-100 rounded-full p-1 mb-6">
            {(["signup", "login"] as AuthMode[]).map((m) => (
              <button
                key={m}
                onClick={() => switchMode(m)}
                className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  mode === m ? "bg-white text-gray-900" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {m === "signup" ? "Sign Up" : "Sign In"}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (signup only) */}
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Your Name</label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name or nickname"
                    className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-pink-400 transition-colors"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
            )}

            {/* Contact method */}
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">Sign in with</label>
              <div className="flex gap-2 mb-3">
                {(["email", "phone"] as ContactMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMethod(m); setErrors({}); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      method === m
                        ? "border-pink-400 bg-pink-50 text-pink-700"
                        : "border-stone-200 text-stone-500 hover:border-stone-300"
                    }`}
                  >
                    <i className={m === "email" ? "ri-mail-line" : "ri-phone-line"}></i>
                    {m === "email" ? "Email" : "Phone"}
                  </button>
                ))}
              </div>
              <div className="relative">
                <i className={`${method === "email" ? "ri-mail-line" : "ri-phone-line"} absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm`}></i>
                <input
                  type={method === "email" ? "email" : "tel"}
                  value={method === "email" ? email : phone}
                  onChange={(e) => method === "email" ? setEmail(e.target.value) : setPhone(e.target.value)}
                  placeholder={method === "email" ? "your@email.com" : "+1 (555) 000-0000"}
                  className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-pink-400 transition-colors"
                />
              </div>
              {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
            </div>

            {/* Role (signup only) */}
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Your Role</label>
                <div className="relative">
                  <i className="ri-briefcase-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-pink-400 bg-white cursor-pointer appearance-none"
                  >
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* WhatsApp (signup only, optional) */}
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  WhatsApp Number <span className="text-stone-400 font-normal">(optional — share to connect with others)</span>
                </label>
                <div className="relative">
                  <i className="ri-whatsapp-line absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-sm"></i>
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-green-400 transition-colors"
                  />
                </div>
                <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                  <i className="ri-shield-check-line text-green-400"></i>
                  Only shown to people you choose to connect with
                </p>
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-stone-600">Password</label>
                {mode === "login" && (
                  <button type="button" className="text-xs text-pink-500 hover:underline cursor-pointer">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-9 pr-10 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-pink-400 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm password (signup only) */}
            {mode === "signup" && (
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-pink-400 transition-colors"
                  />
                </div>
                {errors.confirm && <p className="text-red-500 text-xs mt-1">{errors.confirm}</p>}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm mt-2"
            >
              {mode === "signup" ? "Join Community — Free" : "Sign In"}
            </button>

            {mode === "signup" && (
              <p className="text-xs text-stone-400 text-center">
                By joining, you agree to our{" "}
                <span className="text-pink-500 cursor-pointer hover:underline">Terms</span>{" "}
                &amp;{" "}
                <span className="text-pink-500 cursor-pointer hover:underline">Privacy Policy</span>
              </p>
            )}
          </form>

          {/* Free platform note */}
          <div className="mt-5 bg-pink-50 rounded-xl p-3 flex items-start gap-2">
            <i className="ri-heart-fill text-pink-400 mt-0.5 flex-shrink-0 text-sm"></i>
            <p className="text-pink-600 text-xs leading-relaxed">
              This platform is <strong>100% free</strong> for everyone. Connect, help, and communicate freely. If you are blessed, welcome to donate for infants in need.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
